import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';
const AUTH_TOKEN_KEY = 'novamatch_auth_token';

const api = axios.create({
  baseURL: `${API_URL}/api/auth`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests if it exists
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401: clear token and redirect; use query when session was replaced elsewhere
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      const code = error.response?.data?.code;
      const isSessionReplaced = code === 'SESSION_REPLACED';
      window.location.href = isSessionReplaced ? '/?session_replaced=1' : '/';
    }
    return Promise.reject(error);
  }
);

export const authService = {
  // Register new user
  async register(email, password, firstName, lastName) {
    try {
      const response = await api.post('/register', {
        email,
        password,
        firstName,
        lastName
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },
  // Login with email and password
  async login(email, password) {
    try {
      const response = await api.post('/login', { email, password });
      // Automatically store token if received
      if (response.data.token) {
        localStorage.setItem(AUTH_TOKEN_KEY, response.data.token);
      }
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  // Verify 2FA token
  async verify2FA(userId, token) {
    try {
      const response = await api.post('/verify-2fa', { userId, token });
      if (response.data.token) {
        localStorage.setItem(AUTH_TOKEN_KEY, response.data.token);
      }
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  // Setup 2FA
  async setup2FA(userId) {
    try {
      const response = await api.post('/setup-2fa', { userId });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  // Request password reset
  async forgotPassword(email) {
    try {
      const response = await api.post('/forgot-password', { email });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  // Reset password with token
  async resetPassword(token, password) {
    try {
      const response = await api.patch(`/reset-password/${token}`, { password });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  // Change password
  async changePassword(currentPassword, newPassword) {
    try {
      const response = await api.patch('/change-password', {
        currentPassword,
        newPassword
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  // Check if user is authenticated
  isAuthenticated() {
    return !!localStorage.getItem(AUTH_TOKEN_KEY);
  },

  // Get current user profile
  async getProfile() {
    try {
      const response = await api.get('/profile');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  // Get authentication token
  getToken() {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  },

  /**
   * Clear token and redirect to login with session_replaced param.
   * Call when any API returns 401 with code SESSION_REPLACED (single active session).
   */
  clearTokenAndRedirectSessionReplaced() {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    window.location.href = '/?session_replaced=1';
  },

  // Logout
  async logout() {
    try {
      // Call backend logout endpoint
      await api.post('/logout');
    } catch (error) {
    } finally {
      // Always clear local storage, even if the API call fails
      localStorage.removeItem(AUTH_TOKEN_KEY);
      window.location.href = '/';
    }
  },

  // Re-authenticate to extend session (keeps same user, refreshes token)
  async reauth() {
    try {
      const response = await api.post('/reauth');
      if (response.data?.token) {
        localStorage.setItem(AUTH_TOKEN_KEY, response.data.token);
      }
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  // Exchange NovaBotX one-time SSO code for a NovaMatch session
  async exchangeSsoCode(code) {
    try {
      // Use a bare request so failed SSO does not force-redirect via auth interceptors
      const response = await axios.post(
        `${API_URL}/api/auth/sso/exchange`,
        { code },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (response.data?.token) {
        localStorage.setItem(AUTH_TOKEN_KEY, response.data.token);
      }
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.error
        || error.response?.data?.message
        || null;
      if (message) throw new Error(message);
      throw this.handleError(error);
    }
  },

  // Verify email address
  async verifyEmail(token) {
    try {
      const response = await api.get(`/verify-email/${token}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  // Error handler
  handleError(error) {
    if (error.response?.data?.message) {
      return new Error(error.response.data.message);
    }
    if (error.code === 'ERR_NETWORK') {
      return new Error('Unable to connect to the server. Please check your internet connection.');
    }
    if (error.response?.status === 401) {
      return new Error('Invalid credentials or unauthorized access.');
    }
    if (error.response?.status === 404) {
      return new Error('The requested resource was not found.');
    }
    if (error.response?.status === 500) {
      return new Error('A server error occurred. Please try again later.');
    }
    return new Error('An unexpected error occurred. Please try again.');
  }
};

export default authService;
